import { ROTULO_PERFIL, type Canal, type Eu } from "@horofacial/shared";
import { Link, Navigate, Outlet, useRouterState } from "@tanstack/react-router";
import { ChevronRight, Ellipsis, LockKeyhole, LogOut, PanelLeft, Search, ShieldCheck, UserRound, WifiOff, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { clsx } from "clsx";
import { inicioDoPerfil, podeVer, saidaVoluntaria, useEu, useSair } from "../lib/sessao";
import { useProduto } from "../lib/produto";
import { marcarSessao, sincronizarAparencia } from "../lib/tema";
import { preCarregarTelas } from "../lib/telas";
import { useCanaisCaidos } from "../lib/whatsapp";
import { useMudarDeClinica } from "../lib/plataforma";
import { ATALHOS_CELULAR, MENU, TODOS_ITENS, type GrupoMenu, type ItemMenu } from "../menu";
import { EntradaSobreposta, PaginaEntrar, PaginaEtapa } from "../paginas/Entrada";
import { VERSAO } from "../lib/versao";
import { acoesNovo } from "./acoes";
import { PainelAparencia } from "./Aparencia";
import { classeIcone, classeNovo, classeUsuario, ConteudoAvisos, ConteudoNovo, ConteudoUsuario, rotuloAvisos, rotuloUsuario } from "./casca-gatilhos";
import { Carregando, FaixaGlobal, FalhaCarregar } from "./ui";

// A busca ⌘K (cmdk) só é baixada quando abre; os menus suspensos (Radix), logo depois da primeira tela.
const Comando = lazy(() => import("./Comando"));
const MenuAvisos = lazy(() => import("./MenusCasca").then((m) => ({ default: m.MenuAvisos })));
const MenuNovo = lazy(() => import("./MenusCasca").then((m) => ({ default: m.MenuNovo })));
const MenuUsuario = lazy(() => import("./MenusCasca").then((m) => ({ default: m.MenuUsuario })));
const PaginaPlataforma = lazy(() => import("../paginas/Plataforma").then((m) => ({ default: m.PaginaPlataforma })));

/**
 * Estrutura da aplicação (layout v6, Sidebar "inset" do shadcn/ui):
 * - desktop (≥1024px): menu lateral de 256 px sobre a faixa cinza e o conteúdo num painel arredondado ao lado;
 *   o botão da barra superior (ou Ctrl+B / ⌘B) recolhe o menu ao trilho de ícones;
 * - tablet (600–1023px): trilho de ícones; o mesmo botão mostra o menu inteiro por cima;
 * - celular (<600px): barra inferior com atalhos e a folha "Mais".
 * A barra superior tem a trilha (grupo › tela), a busca ⌘K, os avisos e o botão "Novo".
 */
/** Páginas sem login (conferência de documento pelo QR, anamnese respondida pelo paciente). */
export const CAMINHOS_PUBLICOS = ["/conferir/", "/responder/"];

export function Casca() {
  const caminho = useCaminho();
  return CAMINHOS_PUBLICOS.some((c) => caminho.startsWith(c)) ? <Outlet /> : <CascaInterna />;
}

function CascaInterna() {
  const eu = useEu();
  const consultas = useQueryClient();
  const caminho = useCaminho();
  // Conversa aberta no celular ocupa a tela toda (o campo de mensagem fica onde estaria a barra inferior).
  const conversaAberta = useRouterState({ select: (s) => s.location.pathname === "/conversas" && !!(s.location.search as { c?: string }).c });
  const celular = useCelular();
  const online = useOnline();
  const telaCheia = conversaAberta && celular;
  const completo = eu.data?.etapa === "ok" ? eu.data : null;
  // Último usuário com sessão completa. Se a sessão cair no meio do uso (401), a tela fica montada,
  // com o que foi digitado, e a entrada aparece por cima dela.
  const [anterior, setAnterior] = useState<Eu | null>(null);
  const reentrada = !!anterior && !completo && !saidaVoluntaria();
  const menuLateral = useMenuRecolhido();
  // Tablet: menu inteiro por cima do conteúdo, aberto pelo botão da barra superior.
  const [menuAberto, setMenuAberto] = useState(false);
  const [buscando, setBuscando] = useState(false);
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        setBuscando((b) => !b);
      }
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, []);
  // No painel da plataforma o master não está em clínica nenhuma: nada de WhatsApp.
  const noPainel = !!completo?.master && !completo.master.clinica;
  const caidos = useCanaisCaidos(!noPainel && (podeVer(completo, "/conversas") || podeVer(completo, "/whatsapp")));

  // Entrada e etapas do login no tema escuro; dentro do sistema (mesmo com a sessão caída por cima), o tema escolhido.
  const dentro = !!completo || reentrada;
  const sabe = eu.isSuccess || !!anterior;
  useEffect(() => {
    if (sabe) marcarSessao(dentro);
  }, [sabe, dentro]);
  // A aparência vem do servidor junto com o "eu" (e muda se a clínica trocar a cor).
  useEffect(() => {
    if (completo) sincronizarAparencia(completo);
  }, [completo]);

  useEffect(() => {
    if (!completo) return;
    if (anterior && anterior.usuario.id !== completo.usuario.id) {
      // Entrou outra pessoa: recomeça do início, sem a tela (e os dados) de quem estava antes.
      window.location.assign("/");
      return;
    }
    if (anterior) void consultas.invalidateQueries();
    setAnterior(completo);
    // Só a troca de usuário importa aqui; o restante da sessão muda sem recarregar a tela.
  }, [completo?.usuario.id]);

  useEffect(() => {
    if (!completo) return;
    const id = setTimeout(() => void preCarregarTelas(), 2000);
    return () => clearTimeout(id);
  }, [!!completo]);

  if (eu.isPending && !anterior) return <Carregando texto="Abrindo…" />;
  if (eu.isError && !anterior) return <div className="p-6"><FalhaCarregar erro={eu.error} tentarDeNovo={() => eu.refetch()} /></div>;
  if (!completo && !reentrada) return eu.data ? <PaginaEtapa eu={eu.data} /> : <PaginaEntrar />;
  // Master fora de uma clínica: só o painel da plataforma.
  if (completo && noPainel) {
    return (
      <Suspense fallback={<Carregando texto="Abrindo…" />}>
        <PaginaPlataforma eu={completo} />
      </Suspense>
    );
  }

  const usuario = completo ?? anterior!;
  const menu = menuPermitido(usuario);
  const alertas: Alertas = caidos.length ? { "/conversas": "WhatsApp desconectado" } : {};
  const faixa = caidos.length > 0 && caminho !== "/whatsapp" ? <FaixaWhatsapp canais={caidos} podeReconectar={podeVer(usuario, "/whatsapp")} /> : null;
  return (
    <FaixaGlobal.Provider value={faixa}>
      <div className="min-h-dvh tab:bg-menu">
        {!online && (
          <div role="status" className="fixed inset-x-0 top-0 z-[65] bg-aviso-suave px-4 py-2 text-center text-sm font-medium text-aviso shadow-md">
            Sem internet. O que está na tela continua visível; para salvar, espere a conexão voltar.
          </div>
        )}
        <div inert={reentrada || undefined}>
          <a href="#conteudo" className="botao botao-primario sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[70]">
            Pular para o conteúdo
          </a>
          <MenuLateral eu={usuario} menu={menu} alertas={alertas} recolhido={menuLateral.recolhido} aberto={menuAberto} setAberto={setMenuAberto} />
          {/* Faixa que cobre o conteúdo que rola acima do painel (a barra superior para 8 px abaixo do topo). */}
          <div className={clsx("fixed inset-x-0 top-0 z-[19] hidden h-2 bg-menu tab:block tab:left-16", !menuLateral.recolhido && "lg:left-64")} aria-hidden />
          <div className={clsx("tab:py-2 tab:pr-2 tab:pl-16", !menuLateral.recolhido && "lg:pl-64")}>
            <div className="min-h-dvh bg-fundo tab:min-h-[calc(100dvh-1rem)] tab:rounded-[var(--raio-grande)] tab:border tab:border-border tab:shadow-[var(--sombra-cartao)]">
              {!telaCheia && (
                <BarraSuperior
                  eu={usuario}
                  caminho={caminho}
                  caidos={caidos}
                  aoMenu={() => (matchMedia("(min-width: 64rem)").matches ? menuLateral.alternar() : setMenuAberto(!menuAberto))}
                  aoBuscar={() => setBuscando(true)}
                />
              )}
              <main id="conteudo" tabIndex={-1} className={clsx("outline-none tab:pb-8", !telaCheia && "pb-[calc(5.5rem+env(safe-area-inset-bottom))]")}>
                {caminho === "/" && inicioDoPerfil(usuario) !== "/" ? <Navigate to={inicioDoPerfil(usuario)} replace /> : podeVer(usuario, caminho) ? <Outlet /> : <SemAcesso />}
              </main>
            </div>
          </div>
          {!telaCheia && <BarraInferior eu={usuario} menu={menu} alertas={alertas} />}
        </div>
        {buscando && (
          <Suspense fallback={null}>
            <Comando eu={usuario} aoFechar={() => setBuscando(false)} />
          </Suspense>
        )}
        {reentrada && <EntradaSobreposta eu={eu.data ?? null} />}
      </div>
    </FaixaGlobal.Provider>
  );
}

/** Marca de atenção por item do menu (caminho → motivo, lido também pelo leitor de tela). */
type Alertas = Partial<Record<string, string>>;

/**
 * Barra superior do painel: recolher o menu, trilha (grupo › tela), busca ⌘K, avisos e "Novo".
 * Fica presa no alto ao rolar; no celular mostra a logomarca no lugar da trilha.
 */
function BarraSuperior({ eu, caminho, caidos, aoMenu, aoBuscar }: { eu: Eu; caminho: string; caidos: Canal[]; aoMenu: () => void; aoBuscar: () => void }) {
  const grupo = MENU.find((g) => g.itens.some((i) => ativo(caminho, i.para) && i.para !== "/") || (caminho === "/" && g.itens.some((i) => i.para === "/")));
  const item = TODOS_ITENS.filter((i) => ativo(caminho, i.para)).sort((a, b) => b.para.length - a.para.length)[0];
  const acoes = acoesNovo(eu);
  const atalho = /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘K" : "Ctrl+K";
  // Clique antes de o menu terminar de carregar: ele abre assim que chega.
  const [pedido, setPedido] = useState<"avisos" | "novo" | null>(null);
  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-1.5 border-b border-border bg-fundo/95 px-3 pt-[env(safe-area-inset-top)] backdrop-blur-sm tab:top-2 tab:rounded-t-[var(--raio-grande)] md:px-4">
      <button type="button" className={clsx(classeIcone, "max-tab:hidden")} onClick={aoMenu} aria-label="Recolher ou mostrar o menu" title="Recolher ou mostrar o menu (Ctrl+B / ⌘B)">
        <PanelLeft />
      </button>
      <span className="mx-1 hidden h-4 w-px bg-border tab:block" aria-hidden />
      <span className="tab:hidden">
        <MarcaClinica eu={eu} />
      </span>
      <nav aria-label="Você está em" className="hidden min-w-0 items-center gap-1.5 text-sm tab:flex">
        {grupo && <span className="truncate text-muted-foreground">{grupo.titulo}</span>}
        {grupo && item && <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />}
        {item && <span className="truncate font-medium">{item.rotulo}</span>}
      </nav>
      {eu.master?.clinica && <FaixaMaster eu={eu} />}
      <span className="flex-1" />
      <button
        type="button"
        onClick={aoBuscar}
        className={clsx(
          "hidden h-9 items-center gap-2 rounded-[var(--raio)] bg-muted px-3 text-sm text-muted-foreground transition-colors hover:text-foreground md:flex",
          // Com a faixa do master, a busca encolhe para a faixa caber inteira.
          eu.master?.clinica ? "w-[min(18rem,22vw)]" : "w-[min(30rem,36vw)]",
        )}
      >
        <Search className="size-4 shrink-0" aria-hidden />
        <span className="truncate">Buscar paciente, tela ou ação</span>
        <kbd className="ml-auto rounded border border-border bg-fundo px-1.5 font-sans text-xs leading-[18px]">{atalho}</kbd>
      </button>
      <span className="flex-1" />
      <button type="button" className={clsx(classeIcone, "md:hidden")} onClick={aoBuscar} aria-label="Buscar">
        <Search />
      </button>
      <Suspense
        fallback={
          <button type="button" className={classeIcone} aria-label={rotuloAvisos(caidos)} onClick={() => setPedido("avisos")}>
            <ConteudoAvisos caidos={caidos} />
          </button>
        }
      >
        <MenuAvisos eu={eu} caidos={caidos} abrir={pedido === "avisos"} />
      </Suspense>
      {acoes.length > 0 && (
        <Suspense
          fallback={
            <button type="button" className={classeNovo} aria-label="Novo" onClick={() => setPedido("novo")}>
              <ConteudoNovo />
            </button>
          }
        >
          <MenuNovo eu={eu} aoBuscar={aoBuscar} abrir={pedido === "novo"} />
        </Suspense>
      )}
    </header>
  );
}

/**
 * Master dentro de uma clínica: fica na barra superior, presa no alto em qualquer tela, com a clínica e o usuário
 * que ele está vendo. "Painel" volta à lista de clínicas (para trocar de clínica ou de usuário).
 */
function FaixaMaster({ eu }: { eu: Eu }) {
  const mudar = useMudarDeClinica();
  const { clinica, comoUsuario } = eu.master!;
  const texto = `Master · ${clinica!.nome}${comoUsuario ? ` · como ${comoUsuario.nome} (${ROTULO_PERFIL[comoUsuario.perfil].toLowerCase()})` : ""}`;
  return (
    <div role="status" className="ml-1 flex max-w-[min(28rem,40vw)] min-w-0 items-center gap-1.5 rounded-full bg-aviso-suave py-1 pr-1 pl-2.5 text-[13px] text-aviso lg:shrink-0" title={texto}>
      <ShieldCheck className="size-4 shrink-0" aria-hidden />
      <span className="truncate font-medium max-sm:sr-only">{texto}</span>
      <span className="font-medium sm:hidden" aria-hidden>
        Master
      </span>
      <button type="button" className="shrink-0 rounded-full bg-fundo px-2.5 leading-6 font-medium text-texto hover:bg-superficie-2 pointer-coarse:leading-8" disabled={mudar.isPending} onClick={() => mudar.mutate(null)}>
        Painel
      </button>
    </div>
  );
}

function FaixaWhatsapp({ canais, podeReconectar }: { canais: Canal[]; podeReconectar: boolean }) {
  // Com muitos números caídos, a lista de nomes tomaria a primeira tela do celular: vira contagem (os nomes ficam em WhatsApp).
  const nomes = canais.length > 3 ? null : canais.map((c) => c.nome).join(", ");
  return (
    <div role="status" className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-[var(--raio)] bg-aviso-suave px-3 py-1.5 text-[13px] text-aviso">
      <WifiOff className="size-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1 basis-48">
        <span className="font-medium">{canais.length === 1 ? `WhatsApp ${nomes} desconectado` : nomes ? `WhatsApp desconectado: ${nomes}` : `${canais.length} números de WhatsApp desconectados`}</span>
        <span className="max-md:hidden">: mensagens de pacientes podem não chegar.</span>
      </span>
      {podeReconectar ? (
        <Link to="/whatsapp" className="botao botao-secundario h-8 min-h-8 px-3 text-[13px] text-texto pointer-coarse:h-11 pointer-coarse:min-h-11">
          Reconectar
        </Link>
      ) : (
        <span className="max-sm:basis-full max-sm:pl-7">Avise quem cuida do WhatsApp da clínica.</span>
      )}
    </div>
  );
}

function useOnline() {
  return useSyncExternalStore(
    (avisar) => {
      window.addEventListener("online", avisar);
      window.addEventListener("offline", avisar);
      return () => {
        window.removeEventListener("online", avisar);
        window.removeEventListener("offline", avisar);
      };
    },
    () => navigator.onLine,
  );
}

/** Menu só com o que o usuário pode abrir; grupos vazios somem. */
function menuPermitido(eu: Eu): GrupoMenu[] {
  const semInicio = inicioDoPerfil(eu) !== "/";
  return MENU.map((g) => ({ ...g, itens: g.itens.filter((i) => podeVer(eu, i.para) && !(semInicio && i.para === "/")) })).filter((g) => g.itens.length > 0);
}

function SemAcesso() {
  return (
    <div className="mx-auto mt-16 max-w-md px-4">
      <div className="cartao flex flex-col items-center gap-3 px-6 py-12 text-center">
        <LockKeyhole className="size-8 text-texto-fraco" />
        <h1 className="font-semibold">Sem acesso a esta área</h1>
        <p className="text-sm text-texto-suave">Peça ao administrador da clínica para liberar este menu no seu usuário.</p>
        <Link to="/" className="botao botao-primario">
          Ir para o início
        </Link>
      </div>
    </div>
  );
}

function ativo(caminhoAtual: string, para: string) {
  return para === "/" ? caminhoAtual === "/" : caminhoAtual === para || caminhoAtual.startsWith(`${para}/`);
}

function useMidia(consulta: string) {
  const [casa, setCasa] = useState(() => matchMedia(consulta).matches);
  useEffect(() => {
    const m = matchMedia(consulta);
    const ouvir = () => setCasa(m.matches);
    m.addEventListener("change", ouvir);
    return () => m.removeEventListener("change", ouvir);
  }, [consulta]);
  return casa;
}

const useCelular = () => useMidia("(max-width: 37.49rem)");

function useCaminho() {
  return useRouterState({ select: (s) => s.location.pathname });
}

const CHAVE_RECOLHIDO = "hf:menu-recolhido";
const chaveGrupos = (usuarioId: string) => `hf:menu-grupos:${usuarioId}`;

function lerArmazenado<T>(chave: string, padrao: T): T {
  try {
    const v = localStorage.getItem(chave);
    return v === null ? padrao : (JSON.parse(v) as T);
  } catch {
    return padrao;
  }
}
function gravarArmazenado(chave: string, valor: unknown) {
  try {
    localStorage.setItem(chave, JSON.stringify(valor));
  } catch {
    // Navegação privada ou armazenamento bloqueado: a escolha vale só até recarregar.
  }
}

/** Menu recolhido no computador (só ícones), lembrado no aparelho e alternado por Ctrl+B / ⌘B. */
function useMenuRecolhido() {
  const [recolhido, setRecolhido] = useState(() => lerArmazenado(CHAVE_RECOLHIDO, false));
  const alternar = () =>
    setRecolhido((r) => {
      gravarArmazenado(CHAVE_RECOLHIDO, !r);
      return !r;
    });
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== "b" || !(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return;
      // Em campo de texto, Ctrl+B fica com o campo.
      const alvo = e.target as HTMLElement | null;
      if (alvo?.closest("input, textarea, select, [contenteditable]")) return;
      e.preventDefault();
      alternar();
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, []);
  return { recolhido, alternar };
}

/**
 * Grupo que abre e fecha: tem mais de um item e não é o fixo (Atendimento). Com menu curto
 * (recepção, profissional) tudo fica à mostra: recolher só custaria um clique a mais.
 */
const MAXIMO_SEM_GRUPOS = 9;
const recolhivel = (g: GrupoMenu, menu: GrupoMenu[]) => !g.fixo && g.itens.length > 1 && menu.reduce((n, x) => n + x.itens.length, 0) > MAXIMO_SEM_GRUPOS;
const grupoAtivo = (g: GrupoMenu, caminho: string) => g.itens.some((i) => ativo(caminho, i.para));
const alertaDoGrupo = (g: GrupoMenu, alertas: Alertas) => g.itens.map((i) => alertas[i.para]).find(Boolean);

function MenuLateral({ eu, menu, alertas, recolhido, aberto, setAberto }: { eu: Eu; menu: GrupoMenu[]; alertas: Alertas; recolhido: boolean; aberto: boolean; setAberto: (a: boolean) => void }) {
  const caminho = useCaminho();
  const desktop = useMidia("(min-width: 64rem)");
  // No tablet o menu fica no trilho de ícones; o botão da barra superior mostra o menu inteiro por cima do conteúdo.
  // No computador o mesmo botão (ou Ctrl+B / ⌘B) recolhe o menu ao trilho e devolve o espaço à tela.
  const comNomes = desktop ? !recolhido : aberto;
  // Grupos abertos, por usuário; o grupo da tela atual abre sozinho.
  const [abertos, setAbertos] = useState<string[]>(() => lerArmazenado(chaveGrupos(eu.usuario.id), []));
  const [submenu, setSubmenu] = useState<string | null>(null);
  const mudarGrupo = (titulo: string, abrir: boolean) =>
    setAbertos((l) => {
      const n = abrir ? [...new Set([...l, titulo])] : l.filter((t) => t !== titulo);
      gravarArmazenado(chaveGrupos(eu.usuario.id), n);
      return n;
    });
  useEffect(() => {
    setAberto(false);
    setSubmenu(null);
    const atual = menu.find((g) => recolhivel(g, menu) && grupoAtivo(g, caminho));
    if (atual && !abertos.includes(atual.titulo)) mudarGrupo(atual.titulo, true);
  }, [caminho]);
  useEffect(() => {
    if (!aberto) return;
    const tecla = (e: KeyboardEvent) => e.key === "Escape" && setAberto(false);
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [aberto]);
  useEffect(() => setSubmenu(null), [comNomes]);

  return (
    <>
      {aberto && !desktop && <button type="button" aria-label="Recolher o menu" className="fixed inset-0 z-[25] hidden bg-black/30 tab:block" onClick={() => setAberto(false)} />}
      <aside
        aria-label="Menu principal"
        className={clsx(
          "fixed inset-y-0 left-0 z-30 hidden flex-col gap-2 bg-menu p-2 pt-[calc(0.5rem+env(safe-area-inset-top))] tab:flex",
          comNomes ? "w-64" : "w-16",
          aberto && !desktop && "sobreposta border-r border-border",
        )}
      >
        <div className={clsx("flex min-h-12 items-center gap-2.5 rounded-[var(--raio)] p-1.5", !comNomes && "justify-center")}>
          <MarcaClinica eu={eu} />
          {comNomes && !(eu.empresa.logo || eu.empresa.logoEscuro) && (
            <div className="min-w-0 flex-1 leading-tight">
              <div className="truncate text-[14px] font-semibold">{eu.empresa.nomeExibido || eu.empresa.nome}</div>
              <div className="truncate text-xs text-muted-foreground">{ROTULO_PERFIL[eu.usuario.perfil]}</div>
            </div>
          )}
        </div>

        <nav className={clsx("flex flex-1 flex-col gap-4 overflow-y-auto pt-1", comNomes ? "[scrollbar-width:thin]" : "[scrollbar-width:none]")}>
          {menu.map((grupo) => {
            const sanfona = recolhivel(grupo, menu);
            if (!comNomes) {
              // Trilho: o grupo fixo mostra os ícones dos itens; os outros, um ícone que abre o submenu.
              return (
                <div key={grupo.titulo} className="flex flex-col items-center gap-1">
                  {sanfona ? (
                    <IconeDeGrupo grupo={grupo} caminho={caminho} alerta={alertaDoGrupo(grupo, alertas)} aberto={submenu === grupo.titulo} aoAlternar={(a) => setSubmenu(a ? grupo.titulo : null)} />
                  ) : (
                    grupo.itens.map((item) => <ItemLateral key={item.para} item={item} ativo={ativo(caminho, item.para)} comNome={false} alerta={alertas[item.para]} />)
                  )}
                </div>
              );
            }
            const estaAberto = !sanfona || abertos.includes(grupo.titulo) || grupoAtivo(grupo, caminho);
            const idLista = `menu-${grupo.titulo}`;
            const alerta = alertaDoGrupo(grupo, alertas);
            return (
              <div key={grupo.titulo}>
                {sanfona ? (
                  <button
                    type="button"
                    aria-expanded={estaAberto}
                    aria-controls={idLista}
                    onClick={() => mudarGrupo(grupo.titulo, !estaAberto)}
                    className="flex h-8 w-full items-center gap-2 rounded-[calc(var(--raio)-2px)] px-2 text-left text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
                  >
                    <span className="flex-1">{grupo.titulo}</span>
                    {alerta && !estaAberto && <MarcaAlerta texto={alerta} className="" />}
                    <ChevronRight className={clsx("size-3.5 transition-transform", estaAberto && "rotate-90")} aria-hidden />
                  </button>
                ) : (
                  <div className="flex h-8 items-center px-2 text-xs font-medium text-muted-foreground">{grupo.titulo}</div>
                )}
                {estaAberto && (
                  <ul id={idLista} className="flex flex-col gap-0.5">
                    {grupo.itens.map((item) => (
                      <li key={item.para}>
                        <ItemLateral item={item} ativo={ativo(caminho, item.para)} comNome alerta={alertas[item.para]} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>

        <RodapeUsuario eu={eu} comNome={comNomes} />
      </aside>
    </>
  );
}

/** Rodapé do menu lateral: o usuário (o menu com Minha conta, Aparência e Sair carrega depois da primeira tela). */
function RodapeUsuario({ eu, comNome }: { eu: Eu; comNome: boolean }) {
  const [pedido, setPedido] = useState(false);
  return (
    <Suspense
      fallback={
        <button type="button" className={classeUsuario(comNome)} aria-label={rotuloUsuario(eu)} onClick={() => setPedido(true)}>
          <ConteudoUsuario eu={eu} comNome={comNome} />
        </button>
      }
    >
      <MenuUsuario eu={eu} comNome={comNome} abrir={pedido} />
    </Suspense>
  );
}

/** Trilho: ícone do grupo que abre os itens num submenu ao lado; Esc ou clique fora fecha e o foco volta ao ícone. */
function IconeDeGrupo({ grupo, caminho, alerta, aberto, aoAlternar }: { grupo: GrupoMenu; caminho: string; alerta?: string; aberto: boolean; aoAlternar: (aberto: boolean) => void }) {
  const botao = useRef<HTMLButtonElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  const [topo, setTopo] = useState(0);
  const Icone = grupo.icone;
  const doGrupo = grupoAtivo(grupo, caminho);
  const id = `submenu-${grupo.titulo}`;
  useEffect(() => {
    if (!aberto) return;
    const r = botao.current!.getBoundingClientRect();
    // Não passa da borda de baixo da tela.
    setTopo(Math.max(12, Math.min(r.top, innerHeight - (painel.current?.offsetHeight ?? 0) - 12)));
    painel.current?.querySelector<HTMLElement>("a")?.focus();
    const fora = (e: PointerEvent) => {
      if (!painel.current?.contains(e.target as Node) && !botao.current?.contains(e.target as Node)) aoAlternar(false);
    };
    const tecla = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      aoAlternar(false);
      botao.current?.focus();
    };
    document.addEventListener("pointerdown", fora);
    window.addEventListener("keydown", tecla);
    return () => {
      document.removeEventListener("pointerdown", fora);
      window.removeEventListener("keydown", tecla);
    };
  }, [aberto]);
  return (
    <>
      <button
        ref={botao}
        type="button"
        aria-expanded={aberto}
        aria-controls={id}
        aria-label={alerta ? `${grupo.titulo} (${alerta})` : grupo.titulo}
        title={grupo.titulo}
        onClick={() => aoAlternar(!aberto)}
        className={clsx("relative grid size-10 place-items-center rounded-[var(--raio)] transition-colors", doGrupo || aberto ? "bg-superficie text-marca shadow-[var(--sombra-cartao)]" : "text-foreground hover:bg-accent")}
      >
        <Icone className="size-[18px]" />
        {alerta && <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-aviso ring-2 ring-menu" aria-hidden />}
      </button>
      {aberto && (
        <div ref={painel} id={id} className="cartao sobreposta fixed left-[4.5rem] z-40 w-60 p-1.5 text-foreground" style={{ top: topo }}>
          <div className="px-2 pt-1 pb-1.5 text-xs font-medium text-muted-foreground">{grupo.titulo}</div>
          <ul className="flex flex-col gap-0.5">
            {grupo.itens.map((item) => (
              <li key={item.para}>
                <ItemLateral item={item} ativo={ativo(caminho, item.para)} comNome noSubmenu />
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

/** Item do menu (SidebarMenuButton do shadcn). No trilho, só o ícone, com o nome no title e para o leitor de tela. */
function ItemLateral({ item, ativo, comNome, alerta, noSubmenu }: { item: ItemMenu; ativo: boolean; comNome: boolean; alerta?: string; noSubmenu?: boolean }) {
  const Icone = item.icone;
  return (
    <Link
      to={item.para}
      title={alerta ? `${item.rotulo} · ${alerta}` : item.rotulo}
      aria-current={ativo ? "page" : undefined}
      className={clsx(
        "group relative flex items-center gap-2.5 rounded-[calc(var(--raio)-2px)] text-[14px] transition-colors pointer-coarse:min-h-10",
        comNome ? "h-8 px-2" : "size-10 justify-center",
        ativo
          ? noSubmenu
            ? "bg-muted font-medium"
            : "bg-superficie font-medium shadow-[var(--sombra-cartao)] [&>svg]:text-marca"
          : "text-foreground hover:bg-accent",
      )}
    >
      <Icone className="size-4 shrink-0" aria-hidden />
      <span className={comNome ? "truncate leading-tight" : "sr-only"}>{item.rotulo}</span>
      {alerta && <MarcaAlerta texto={alerta} className={comNome ? "ml-auto" : "absolute top-1.5 right-1.5"} />}
      {!item.pronto && comNome && <span className="ml-auto size-1.5 shrink-0 rounded-full bg-texto-fraco/50" title="Em construção" aria-hidden />}
    </Link>
  );
}

function MarcaAlerta({ texto, className }: { texto: string; className: string }) {
  return (
    <>
      <span className={`size-2 shrink-0 rounded-full bg-aviso ring-2 ring-superficie ${className}`} aria-hidden />
      <span className="sr-only">({texto})</span>
    </>
  );
}

function BarraInferior({ eu, menu, alertas }: { eu: Eu; menu: GrupoMenu[]; alertas: Alertas }) {
  const caminho = useCaminho();
  const [maisAberto, setMaisAberto] = useState(false);
  const atalhos = ATALHOS_CELULAR.filter((p) => podeVer(eu, p) && !(p === "/" && inicioDoPerfil(eu) !== "/")).map((p) => TODOS_ITENS.find((i) => i.para === p)!);
  const maisAtivo = !atalhos.some((a) => ativo(caminho, a.para));

  useEffect(() => {
    setMaisAberto(false);
  }, [caminho]);

  return (
    <>
      <nav
        aria-label="Atalhos"
        className="fixed inset-x-0 bottom-0 z-40 flex justify-around border-t border-borda bg-superficie px-1 pt-1 pb-[calc(0.25rem+env(safe-area-inset-bottom))] tab:hidden"
      >
        {atalhos.map((item) => (
          <AtalhoInferior key={item.para} item={item} ativo={ativo(caminho, item.para)} alerta={alertas[item.para]} />
        ))}
        <button
          type="button"
          onClick={() => setMaisAberto(true)}
          className={`flex min-h-14 max-w-24 flex-1 flex-col items-center justify-center gap-0.5 rounded-[var(--raio)] text-xs ${maisAtivo ? "font-semibold text-marca" : "text-texto-suave"}`}
          aria-haspopup="dialog"
          aria-expanded={maisAberto}
        >
          <span className={`grid h-7 w-12 place-items-center rounded-full ${maisAtivo ? "bg-marca-suave" : ""}`}>
            <Ellipsis className="size-5" />
          </span>
          Mais
        </button>
      </nav>
      {maisAberto && <FolhaMais aoFechar={() => setMaisAberto(false)} caminho={caminho} menu={menu} eu={eu} alertas={alertas} />}
    </>
  );
}

function AtalhoInferior({ item, ativo, alerta }: { item: ItemMenu; ativo: boolean; alerta?: string }) {
  const Icone = item.icone;
  return (
    <Link
      to={item.para}
      aria-current={ativo ? "page" : undefined}
      className={`flex min-h-14 max-w-24 flex-1 flex-col items-center justify-center gap-0.5 rounded-[var(--raio)] text-xs transition-colors ${ativo ? "font-semibold text-marca" : "text-texto-suave"}`}
    >
      <span className={`relative grid h-7 w-12 place-items-center rounded-full ${ativo ? "bg-marca-suave" : ""}`}>
        <Icone className="size-5" />
        {alerta && <MarcaAlerta texto={alerta} className="absolute -top-0.5 -right-1" />}
      </span>
      {item.rotulo}
    </Link>
  );
}

function FolhaMais({ aoFechar, caminho, menu, eu, alertas }: { aoFechar: () => void; caminho: string; menu: GrupoMenu[]; eu: Eu; alertas: Alertas }) {
  const sair = useSair();
  const produto = useProduto();
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => e.key === "Escape" && aoFechar();
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [aoFechar]);

  return (
    <div className="fixed inset-0 z-50 tab:hidden" role="dialog" aria-modal="true" aria-label="Todas as seções">
      <button type="button" aria-label="Fechar" className="veu-entrada absolute inset-0 bg-black/25" onClick={aoFechar} />
      <div className="folha-entrada sobreposta absolute inset-x-0 bottom-0 max-h-[85dvh] overflow-y-auto rounded-t-[var(--raio-folha)] border-t border-borda bg-superficie p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        <div className="flex items-center justify-between px-2 pb-2">
          <span className="font-semibold">Todas as seções</span>
          <button type="button" onClick={aoFechar} className="botao botao-secundario botao-icone" aria-label="Fechar">
            <X className="size-5" />
          </button>
        </div>
        {menu.map((grupo) => (
          <div key={grupo.titulo} className="mt-2">
            <div className="px-2 pb-1 text-[13px] font-semibold text-texto-suave">
              {grupo.titulo}
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {grupo.itens.map((item) => {
                const Icone = item.icone;
                const estaAtivo = ativo(caminho, item.para);
                return (
                  <Link
                    key={item.para}
                    to={item.para}
                    className={`flex min-h-12 items-center gap-2 rounded-2xl px-3 text-[13px] ${estaAtivo ? "bg-marca-suave font-semibold text-marca" : "bg-preenchimento-3"}`}
                  >
                    <Icone className="size-[18px] shrink-0" />
                    <span className="leading-tight">{item.rotulo}</span>
                    {alertas[item.para] && <MarcaAlerta texto={alertas[item.para]!} className="ml-auto" />}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
        <div className="mt-4 grid grid-cols-2 gap-1.5 border-t border-borda pt-3">
          <Link to="/conta" className="flex min-h-12 items-center gap-2 rounded-2xl bg-preenchimento-3 px-3 text-[13px]">
            <UserRound className="size-[18px] shrink-0" />
            <span className="min-w-0 leading-tight">
              <span className="block truncate">Minha conta</span>
              <span className="block truncate text-xs text-texto-suave">{eu.usuario.nome}</span>
            </span>
          </Link>
          <button type="button" onClick={() => sair.mutate()} className="flex min-h-12 items-center gap-2 rounded-2xl bg-preenchimento-3 px-3 text-[13px] text-perigo">
            <LogOut className="size-[18px] shrink-0" />
            Sair
          </button>
        </div>
        <details className="mt-3 rounded-[var(--raio)] border border-borda px-3 py-2">
          <summary className="flex min-h-10 cursor-pointer items-center text-[13px] font-semibold">Aparência</summary>
          <div className="pt-2 pb-1">
            <PainelAparencia />
          </div>
        </details>
        <p className="mt-3 px-1 text-right text-[12px] text-texto-fraco tabular-nums">
          {produto} v{VERSAO}
        </p>
      </div>
    </div>
  );
}

/**
 * Logomarca da clínica no menu: a versão para fundo claro no modo claro e a de fundo escuro no modo escuro.
 * Com só uma delas, ela vale nos dois modos (a clara vai sobre um quadro branco no escuro). Sem logomarca, o ícone do produto.
 */
function MarcaClinica({ eu }: { eu: Eu }) {
  const { logo, logoEscuro, nomeExibido, nome } = eu.empresa;
  const alt = `Logomarca de ${nomeExibido || nome}`;
  const img = (tipo: "claro" | "escuro", v: string, classe = "") => (
    <img src={`/api/empresa/logo/${tipo}?v=${v}`} alt={alt} className={`h-10 w-auto max-w-[9rem] shrink-0 object-contain ${classe}`} />
  );
  if (logo && logoEscuro) {
    return (
      <>
        {img("claro", logo, "so-claro")}
        {img("escuro", logoEscuro, "so-escuro")}
      </>
    );
  }
  if (logoEscuro) return img("escuro", logoEscuro);
  if (logo) {
    return (
      <span className="grid h-10 max-w-[9rem] shrink-0 place-items-center rounded-[var(--raio)] bg-white px-1.5">
        <img src={`/api/empresa/logo/claro?v=${logo}`} alt={alt} className="max-h-8 w-auto object-contain" />
      </span>
    );
  }
  return <img src="/icone.svg" alt="" className="size-9 shrink-0 rounded-[var(--raio)]" />;
}
